import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { AccountDataServiceProvider } from '@services/user/account.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { ValidationProvider } from '@services/helpers/validation.service';
import { User, UserSubscription } from '@models/models';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { SettingsProvider } from '@services/settings/settings';
import { ReplaySubject } from 'rxjs';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { of } from 'rxjs/observable/of';
import { SettingsOverviewDataProvider } from '@services/settings/settings-overview/settings-overview.data.service';
import { catchError, switchMap } from 'rxjs/operators';
import { BrandCreationData, BrandDetailsInfo } from '@models/brands.model';
import { BrandsDataServiceProvider } from '@services/brands/brands.data.service';
import { AuthTokensService } from '../../auth-tokens.service';

declare const Intercom;

@Injectable()
export class AccountServiceWebProvider {
	authenticated = false;
	subscription: UserSubscription;
	subscriptionData: BehaviorSubject<any> = new BehaviorSubject(null);
	user: User;
	userData: BehaviorSubject<User> = new BehaviorSubject(null);
	navigationData: BehaviorSubject<any> = new BehaviorSubject({});

	private companyId$: ReplaySubject<number> = new ReplaySubject<number>(1);
	private showHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		false
	);
	private userLoginAction$: BehaviorSubject<boolean> = new BehaviorSubject<
		boolean
	>(false);

	private paths = {
		USER_MENU_PATH: 'authenticatedUser',
		USER_HOME_PATH: 'authenticatedUser',
		SETTING_NAVBAR_PATH: 'authenticatedUser',
		ANONYMOUS_MENU_PATH: 'anonymousUser',
		ANONYMOUS_HOME_PATH: 'anonymousUser',
		ADMIN_MENU_PATH: 'adminUser',
		ADMIN_HOME_PATH: 'adminUser'
	};

	constructor(
		private accountDataService: AccountDataServiceProvider,
		private messageService: MessageService,
		private validationService: ValidationProvider,
		private location: Location,
		private settingsService: SettingsProvider,
		private snModalService: SNModalService,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private brandsDataService: BrandsDataServiceProvider,
		private settingsOverviewService: SettingsOverviewDataProvider,
		private storageService: AuthTokensService
	) {}

	get userDataObservable(): Observable<User> {
		return this.userData.asObservable();
	}

	get navDataObservable() {
		return this.navigationData.asObservable();
	}

	get showHeaderObservable(): Observable<boolean> {
		return this.showHeader$.asObservable();
	}

	get userLoginActionObservable(): Observable<boolean> {
		return this.userLoginAction$.asObservable();
	}

	emitUser(user: User): void {
		this.userData.next(user);
	}

	emitSubscriptionInfo(data): void {
		this.subscriptionData.next(data);
	}

	emitShowHeader(value: boolean): void {
		this.showHeader$.next(value);
	}

	emitUserLoginAction(state: boolean): void {
		this.userLoginAction$.next(state);
	}

	getUserWeb(): Observable<User> {
		return this.accountDataService
			.getUser()
			.catch((error): Observable<void> => {
				this.messageService.showDefaultErrorMessage(error);
				return Observable.throw(error);
			})
			.map((data: User): User => this.getUserWebSuccessCallback(data));
	}

	logoutWeb() {
		return this.accountDataService
			.logout()
			.catch((error): Observable<void> => {
				this.messageService.showDefaultErrorMessage(error);
				return Observable.throw(error);
			})
			.subscribe((): void => this.successLogoutWeb());
	}

	successLogoutWeb(): void {
		this.setUserAuthenticated(false);
		this.setNavigationSettings(null, null, null);
		this.emitUser(null);
		this.subscriptionService.setSubscriptionData(null);
		this.snModalService.closeActiveModal();
		this.storageService.removeTokens();
		delete this.user;
		this.location.go('/login');
		Intercom('shutdown');
	}

	isUserAuthenticated(): boolean {
		return this.authenticated;
	}

	isAdmin(): boolean {
		return this.user && this.user.admin;
	}

	setUserAuthenticated(isAuthenticated: boolean): void {
		this.authenticated = isAuthenticated;
	}

	authenticateWeb(
		email: string,
		password: string,
		stayLogin: boolean
	): Observable<any> {
		// ): Observable<HttpEvent<any>> {
		if (
			this.validationService.isStringEmpty(password) ||
			this.validationService.isStringEmpty(email)
		) {
			this.messageService.showToaster('e_0', 'login');
			return Observable.throw(new Error('validation error'));
		}
		return (
			this.accountDataService
				.authenticateWeb(email, password, stayLogin)
				// .map(data => this.getUserWebSuccessCallback(data))
				.catch((error): Observable<void> => this.errorAuthenticate(error))
		);
	}

	getRefreshedAuthTokenWeb(token: string): Observable<any> {
		return this.accountDataService.getRefreshedAuthTokenWeb(token);
	}

	signUp(signUpData, signUpSuccessCallback, signUpErrorCallback) {
		this.accountDataService
			.signUp(signUpData)
			.subscribe(
				data => signUpSuccessCallback(data),
				result => signUpErrorCallback(result)
			);
	}

	confirmSignUp(signUpSuccessCallback, signUpErrorCallback) {
		const url = this.location.path();
		this.accountDataService
			.confirmSignUp(url)
			.subscribe(
				result => signUpSuccessCallback(result),
				result => signUpErrorCallback(result)
			);
	}

	companyRegister(registrationData, successCallback, errorCallback) {
		this.accountDataService
			.signUp(registrationData)
			.subscribe(
				result => successCallback(result),
				result => errorCallback(result)
			);
	}

	setNavigationSettings = (
		menuPath: string,
		homePath?: string,
		navbarPath = 'authenticatedUser'
	) => {
		const path = this.location.path();
		if (path.includes('/subscription') || path.includes('/reactivate')) {
			menuPath = null;
			homePath = null;
		}

		this.navigationData.next({
			menu_path: menuPath,
			home_path: homePath || menuPath,
			settings_navbar_path: navbarPath
		});
	};

	getInvitationCompany() {
		const confirmPathPart = 'register/';
		const url = this.location.path();
		const index = url.indexOf(confirmPathPart);
		let token = null;

		if (index !== -1 && index + confirmPathPart.length < url.length) {
			token = url.substring(confirmPathPart.length + index, url.length);
		}

		if (token) {
			return this.accountDataService.getInvitationCompany(token);
		}
	}

	saveBrand(brand: BrandCreationData): Observable<BrandDetailsInfo> {
		if (!brand.toImportData && brand.toImportData !== null) {
			brand.toImportData = null;
		}

		return this.brandsDataService.saveBrand(brand);
	}

	updateSystemDueToBrandChange(): Observable<void> {
		return of(null).pipe(
			switchMap(() => this.subscriptionService.updateStatus()),
			switchMap(() => this.getUserWeb()),
			switchMap(() => this.brandsDataService.updateBrands()),
			catchError(err => {
				this.messageService.showDefaultErrorMessage(err);
				return of(null);
			})
		);
	}

	private errorAuthenticate(result): Observable<any> {
		const { error } = result;
		if (error.error === 'unauthorized' || error.error === 'invalid_grant') {
			this.messageService.showToaster('e_0', 'login');
		} else {
			this.messageService.showToaster('e_0', 'general');
		}
		return Observable.throw(error);
	}

	bootIntercom(): void {
		const app_id =
			window.location.hostname !== 'app.studioninja.co'
				? 'wa5e1gxe'
				: 'puddxy5b';
		Intercom('boot', { app_id });
	}

	get companyIdObservable(): ReplaySubject<number> {
		return this.companyId$;
	}

	private getUserWebSuccessCallback(data: User): User {
		if (!data) {
			console.error('No user data');
			return;
		}

		const {
			USER_MENU_PATH,
			USER_HOME_PATH,
			SETTING_NAVBAR_PATH,
			ADMIN_MENU_PATH,
			ADMIN_HOME_PATH
		} = this.paths;

		this.user = data;
		const isAdmin = data.admin;
		if (isAdmin) {
			this.setUserAuthenticated(true);
			this.settingsService.setDefaultTimeFormatSettings();
			this.setNavigationSettings(
				ADMIN_MENU_PATH,
				ADMIN_HOME_PATH,
				SETTING_NAVBAR_PATH
			);
		} else if (data.termsOfUseAgree !== true) {
			this.setUserAuthenticated(true);
			this.settingsService.getSettingsRequest().subscribe();
			this.settingsOverviewService.getSettingsOverviewItems().subscribe();
			if (this.location.path() !== '/login') {
				this.logoutWeb();
			}
		} else if (data.enabled) {
			this.bootIntercom();
			this.setUserAuthenticated(true);
			this.settingsService.getSettingsRequest().subscribe();
			this.settingsOverviewService.getSettingsOverviewItems().subscribe();
			this.setNavigationSettings(
				USER_MENU_PATH,
				USER_HOME_PATH,
				SETTING_NAVBAR_PATH
			);
			this.updateIntercom(data);
		} else {
			console.error('Anonymous User');
		}

		if (data.intercomCompany && data.intercomCompany.id) {
			this.companyId$.next(data.intercomCompany.id);
		}
		this.emitUser(this.user);

		return data;
	}

	private updateIntercom(data): void {
		const { id, firstName, email, registeredAt, intercomCompany } = data;
		Intercom('update', {
			user_id: id,
			name: firstName,
			email,
			created_at: registeredAt,
			plan: intercomCompany.subscriptionStatus, // this must be customer attribute
			company: {
				id: intercomCompany.id,
				name: intercomCompany.name,
				email: intercomCompany.email,
				created_at: intercomCompany.createdAt,
				plan: intercomCompany.plan,
				jobs: intercomCompany.jobsCount,
				invoices: intercomCompany.invoiceCount,
				quotes: intercomCompany.quoteCount,
				contracts: intercomCompany.contractCount,
				questionnaires: intercomCompany.questionnaireCount,
				promo_code: intercomCompany.companyPromoCode
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/services/user/account.service.ts